/**
 * factoringStatusStore — single source of truth for factoring CONNECTION state.
 *
 * Before this store, every consumer (onboarding welcome page, Settings ›
 * Integrations › Factoring, accounting pages, agentic hub) called
 * `fetchFactoringStatus()` independently and kept its own local copy. Connecting
 * a provider in one place did NOT update the others until a manual page refresh.
 *
 * This store holds the combined factoring status, the active provider(s) and the
 * Triumph SFTP flag in ONE place. Every consumer subscribes to it, so a connect/
 * disconnect anywhere updates all mounted views live — the same single-source-of-
 * truth model the ELD integrations get for free via `application.integrations`.
 *
 * Sibling to `factoringProviderStore` (which owns the SELECTED rating provider).
 * Connection status is server-derived and intentionally NOT persisted — it is
 * always re-fetched from the backend via `refresh()`.
 */

import { create } from 'zustand'
import { ActiveFactoringProvider } from '@numeo/types-core'
import {
    fetchFactoringStatus,
    fetchActiveFactoringProvider,
    clearFactoringStatusCache,
    FactoringStatusResponse,
    FactoringProviderStatus,
} from 'services/factoring'
import { fetchTriumphSftpStatus } from 'services/factoring/triumph-sftp'

export type FactoringStatusData = NonNullable<FactoringStatusResponse['data']>

// Keys of the status payload that map to a per-provider status object. Excludes scalar
// flags like `factoringEnabled` so spreads/narrowing stay on `FactoringProviderStatus`.
type ProviderStatusKey = Exclude<keyof FactoringStatusData, 'factoringEnabled'>

export interface FactoringActiveProviders {
    main: ActiveFactoringProvider | null
    sftp: ActiveFactoringProvider | null
}

interface FactoringStatusStore {
    /** Combined per-provider status (keyed by provider, e.g. `rts`, `triumph`). */
    status: Partial<FactoringStatusData>
    /** Triumph SFTP lives on a separate endpoint, so it gets its own flag. */
    triumphSftpConnected: boolean
    /** Active provider for the centralized invoice flow (main OAuth/API + SFTP are independent). */
    activeProviders: FactoringActiveProviders
    /** True while a `refresh()` round-trip is in flight. */
    isLoading: boolean
    /** False until the first successful `refresh()` resolves. */
    hasLoaded: boolean
    /** Re-fetch everything from the backend and replace store state. Concurrent calls share one round-trip. */
    refresh: () => Promise<void>
    /** Optimistically patch one provider's status (merges so server-derived fields survive). */
    patchStatus: (key: ProviderStatusKey, value: Partial<FactoringProviderStatus>) => void
    setTriumphSftpConnected: (connected: boolean) => void
    setActiveProviders: (updater: (prev: FactoringActiveProviders) => FactoringActiveProviders) => void
}

// Dedup concurrent refreshes (multiple mounted consumers, visibilitychange, etc.)
// into a single network round-trip.
let inFlight: Promise<void> | null = null

export const useFactoringStatusStore = create<FactoringStatusStore>((set) => ({
    status: {},
    triumphSftpConnected: false,
    activeProviders: { main: null, sftp: null },
    isLoading: false,
    hasLoaded: false,
    refresh: () => {
        if (inFlight) return inFlight
        set({ isLoading: true })
        inFlight = (async () => {
            try {
                // Always bypass the 30s status cache — we want the authoritative server state here.
                clearFactoringStatusCache()
                // Give the SECONDARY endpoints their own fallbacks so a transient failure on one (e.g. a
                // 500 on /factoring/active-provider during a cold start) no longer aborts the group and
                // leaves every provider showing disconnected. The PRIMARY /factoring/status leg is left to
                // reject — that drops to the outer catch, which preserves the last-known status instead of
                // wiping it to {} on a blip. Triumph SFTP runs in parallel so a slow SFTP no longer delays load.
                const [factoringStatus, activeProviders, triumphSftp] = await Promise.all([
                    fetchFactoringStatus('accounting'),
                    fetchActiveFactoringProvider().catch(() => ({ main: null, sftp: null })),
                    fetchTriumphSftpStatus().catch(() => null),
                ])
                set({
                    status: factoringStatus ?? {},
                    activeProviders,
                    triumphSftpConnected: !!triumphSftp?.exists && !!triumphSftp?.isValid,
                    hasLoaded: true,
                })
            } catch (error) {
                console.error('[factoringStatusStore] Failed to refresh factoring status:', error)
            } finally {
                set({ isLoading: false })
                inFlight = null
            }
        })()
        return inFlight
    },
    patchStatus: (key, value) => set((s) => ({ status: { ...s.status, [key]: { ...s.status[key], ...value } } })),
    setTriumphSftpConnected: (connected) => set({ triumphSftpConnected: connected }),
    setActiveProviders: (updater) => set((s) => ({ activeProviders: updater(s.activeProviders) })),
}))

/** Imperative refresh for non-React callers (e.g. after a connect flow completes). */
export const refreshFactoringStatus = (): Promise<void> => useFactoringStatusStore.getState().refresh()

/** Derived: is ANY factoring provider connected & valid (including Triumph SFTP)? */
export const selectAnyFactoringConnected = (s: Pick<FactoringStatusStore, 'status' | 'triumphSftpConnected'>): boolean => {
    if (s.triumphSftpConnected) return true
    return Object.entries(s.status).some(([key, p]) => {
        // `factoringEnabled` is a scalar flag, not a provider status — narrow to objects before reading.
        // Engaged Financial is a public API (always "authenticated"), not a real connection — skip it.
        if (key === 'engaged_financial') return false
        return typeof p === 'object' && p !== null && p.connected === true && p.isValid === true
    })
}
